import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const PlannerContext = createContext(null);

const CALENDAR_CHANGED_EVENT = 'odium:calendar-changed';

export function notifyCalendarChanged(detail = {}) {
  try {
    window.dispatchEvent(new CustomEvent(CALENDAR_CHANGED_EVENT, { detail }));
  } catch (_) {
    /* ignore */
  }
}

export function subscribeCalendarChanged(handler) {
  window.addEventListener(CALENDAR_CHANGED_EVENT, handler);
  return () => window.removeEventListener(CALENDAR_CHANGED_EVENT, handler);
}

/** Events außerhalb des Fetch-Fensters behalten, innerhalb durch Server-Antwort ersetzen. */
function mergeEventsByRange(prev, fetched, from, to) {
  const nextFetched = Array.isArray(fetched) ? fetched : [];
  if (!from || !to) return nextFetched;

  const fromMs = new Date(from).getTime();
  const toMs = new Date(to).getTime();
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs)) return nextFetched;

  const kept = (Array.isArray(prev) ? prev : []).filter((ev) => {
    const startMs = new Date(ev?.startAt).getTime();
    if (!Number.isFinite(startMs)) return true;
    // Im abgefragten Fenster → verwerfen (kommt aus fetched)
    return startMs < fromMs || startMs > toMs;
  });

  const byId = new Map();
  for (const ev of kept) byId.set(String(ev.id), ev);
  for (const ev of nextFetched) byId.set(String(ev.id), ev);

  return [...byId.values()].sort((a, b) =>
    String(a.startAt || '').localeCompare(String(b.startAt || ''))
  );
}

function upsertEvent(prev, event) {
  if (!event?.id) return prev;
  const id = String(event.id);
  const without = (Array.isArray(prev) ? prev : []).filter((e) => String(e.id) !== id);
  return [...without, event].sort((a, b) =>
    String(a.startAt || '').localeCompare(String(b.startAt || ''))
  );
}

export const PlannerProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const userId = user?.id ?? null;

  const [notes, setNotes] = useState([]);
  const [events, setEvents] = useState([]);
  const [preferences, setPreferences] = useState({ postitsAlwaysVisible: false });
  const [loadingNotes, setLoadingNotes] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const refreshGenRef = useRef(0);

  const refreshPreferences = useCallback(async () => {
    if (!isAuthenticated) {
      setPreferences({ postitsAlwaysVisible: false });
      return;
    }
    try {
      const res = await axios.get('/api/planner-preferences');
      setPreferences({ postitsAlwaysVisible: !!res.data?.postitsAlwaysVisible });
    } catch (_) {
      setPreferences({ postitsAlwaysVisible: false });
    }
  }, [isAuthenticated]);

  const refreshNotes = useCallback(async () => {
    if (!isAuthenticated || !userId) {
      setNotes([]);
      setLoadingNotes(false);
      return;
    }
    setLoadingNotes(true);
    try {
      const res = await axios.get('/api/sticky-notes');
      setNotes(Array.isArray(res.data?.notes) ? res.data.notes : []);
    } catch (_) {
      setNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  }, [isAuthenticated, userId]);

  const refreshEvents = useCallback(async (from, to) => {
    if (!isAuthenticated) {
      setEvents([]);
      setLoadingEvents(false);
      return;
    }
    const gen = ++refreshGenRef.current;
    setLoadingEvents(true);
    try {
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      const res = await axios.get('/api/calendar-events', { params });
      if (gen !== refreshGenRef.current) return;
      const fetched = Array.isArray(res.data?.events) ? res.data.events : [];
      setEvents((prev) => mergeEventsByRange(prev, fetched, from, to));
    } catch (_) {
      if (gen !== refreshGenRef.current) return;
      if (!from && !to) setEvents([]);
    } finally {
      if (gen === refreshGenRef.current) setLoadingEvents(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    setNotes([]);
    setEvents([]);
    refreshPreferences();
    refreshNotes();
  }, [userId, isAuthenticated, refreshPreferences, refreshNotes]);

  const updatePreferences = useCallback(async (patch) => {
    const next = { ...preferences, ...patch };
    setPreferences(next);
    const res = await axios.put('/api/planner-preferences', next);
    setPreferences({ postitsAlwaysVisible: !!res.data?.postitsAlwaysVisible });
    return res.data;
  }, [preferences]);

  const createNote = useCallback(async (payload = {}) => {
    const res = await axios.post('/api/sticky-notes', payload);
    const note = res.data?.note;
    if (note) setNotes((prev) => [note, ...prev]);
    return note;
  }, []);

  const updateNote = useCallback(async (id, patch) => {
    setNotes((prev) =>
      prev.map((n) => (String(n.id) === String(id) ? { ...n, ...patch } : n))
    );
    try {
      const res = await axios.put(`/api/sticky-notes/${id}`, patch);
      const note = res.data?.note;
      if (note) {
        setNotes((prev) => prev.map((n) => (String(n.id) === String(id) ? note : n)));
      }
      return note;
    } catch (err) {
      try {
        const res = await axios.get('/api/sticky-notes');
        setNotes(Array.isArray(res.data?.notes) ? res.data.notes : []);
      } catch (_) {
        /* ignore */
      }
      throw err;
    }
  }, []);

  const deleteNote = useCallback(async (id) => {
    await axios.delete(`/api/sticky-notes/${id}`);
    setNotes((prev) => prev.filter((n) => String(n.id) !== String(id)));
  }, []);

  const createEvent = useCallback(async (payload) => {
    const res = await axios.post('/api/calendar-events', payload);
    const event = res.data?.event;
    if (event && (!payload?.recurrence || payload.recurrence === 'none')) {
      setEvents((prev) => upsertEvent(prev, event));
    }
    notifyCalendarChanged({ reason: 'created', id: event?.id });
    return event;
  }, []);

  const updateEvent = useCallback(async (id, patch) => {
    const res = await axios.put(`/api/calendar-events/${id}`, patch);
    const event = res.data?.event;
    if (event && (!patch?.recurrence || patch.recurrence === 'none') && !String(id).includes('::')) {
      setEvents((prev) => upsertEvent(prev, event));
    }
    notifyCalendarChanged({ reason: 'updated', id: event?.id || id });
    return event;
  }, []);

  const deleteEvent = useCallback(async (id) => {
    await axios.delete(`/api/calendar-events/${id}`);
    const seriesKey = String(id).includes('::') ? String(id).split('::')[0] : String(id);
    setEvents((prev) =>
      prev.filter(
        (e) =>
          String(e.id) !== String(id) &&
          String(e.seriesId) !== seriesKey &&
          !String(e.id).startsWith(`${seriesKey}::`)
      )
    );
    notifyCalendarChanged({ reason: 'deleted', id });
  }, []);

  const value = useMemo(
    () => ({
      notes,
      events,
      preferences,
      loadingNotes,
      loadingEvents,
      refreshNotes,
      refreshEvents,
      refreshPreferences,
      updatePreferences,
      createNote,
      updateNote,
      deleteNote,
      createEvent,
      updateEvent,
      deleteEvent,
    }),
    [
      notes,
      events,
      preferences,
      loadingNotes,
      loadingEvents,
      refreshNotes,
      refreshEvents,
      refreshPreferences,
      updatePreferences,
      createNote,
      updateNote,
      deleteNote,
      createEvent,
      updateEvent,
      deleteEvent,
    ]
  );

  return <PlannerContext.Provider value={value}>{children}</PlannerContext.Provider>;
};

export const usePlanner = () => {
  const ctx = useContext(PlannerContext);
  if (!ctx) throw new Error('usePlanner must be used within PlannerProvider');
  return ctx;
};
